import Phaser from "phaser";
import type { MelodySequencer } from "../systems/MelodySequencer.js";
import type { BrickConfig } from "../objects/Brick.js";
import type { Brick } from "../objects/Brick.js";
import { getActiveInsets, getGameviewContentOffset } from "../ui/sceneLayout.js";
import { planChapterGrid, planDungeonGrid, type BrickSpawnPlan } from "./gridBuildPlan.js";
import {
  offsetActiveProjectiles,
  reconcileBricksForGameviewOffset,
} from "./battlefieldGameviewOffset.js";
import { runChapterGridTransition } from "./battlefieldTransition.js";
import type { BattlefieldRuntimePools } from "./battlefieldRuntimePools.js";

export interface BuildChapterGridOptions {
  readonly chapter: number;
  readonly forceMysteryBricks: boolean;
  readonly forceSpecialBricks: boolean;
}

export class BattlefieldGridRuntime {
  private gameviewOffsetY = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly runtime: () => BattlefieldRuntimePools,
    private readonly melodySequencer: MelodySequencer,
    private readonly gameviewMask: () => import("@yjsgames/phaser-ui").RectMask | undefined,
    private readonly enemyScale: () => number = () => 1,
  ) {}

  applyGameviewOffset(n: number): void {
    const delta = n - this.gameviewOffsetY;
    offsetActiveProjectiles(this.runtime().projectiles, delta);
    this.reconcileGameviewOffset();
  }

  reconcileGameviewOffset(): void {
    this.gameviewOffsetY = reconcileBricksForGameviewOffset({
      scene: this.scene,
      bricks: this.runtime().bricks,
      gameviewMask: this.gameviewMask(),
    });
  }

  buildChapterGrid(opts: BuildChapterGridOptions): void {
    const offset = getGameviewContentOffset(this.scene);
    const plan = planChapterGrid(
      opts.chapter,
      this.scene.scale.width,
      getActiveInsets(this.scene),
      offset,
      {
        forceMysteryBricks: opts.forceMysteryBricks,
        forceSpecialBricks: opts.forceSpecialBricks,
      },
      this.enemyScale(),
    );
    this.melodySequencer.setSong(plan.melodyNoteIds);
    this.spawnPlannedBricks(plan.bricks);
    this.reconcileGameviewOffset();
  }

  buildDungeonGrid(index: number): void {
    const offset = getGameviewContentOffset(this.scene);
    const plans = planDungeonGrid(index, this.scene.scale.width, getActiveInsets(this.scene), offset, undefined, this.enemyScale());
    this.spawnPlannedBricks(plans);
    this.reconcileGameviewOffset();
  }

  applyEnemyScale(): void {
    const scale = this.enemyScale();
    for (const obj of this.runtime().bricks.getChildren()) {
      const brick = obj as Brick;
      if (!brick.active) continue;
      brick.applyEnemyScale(scale, true);
    }
  }

  transitionChapterGrid(buildChapterGrid: () => void): void {
    runChapterGridTransition(this.scene, buildChapterGrid);
  }

  private spawnPlannedBricks(plans: BrickSpawnPlan[]): void {
    for (const p of plans) this.spawnBrick(p.x, p.y, p.config);
  }

  private spawnBrick(x: number, y: number, cfg: BrickConfig): void {
    const brick = this.runtime().brickPool.acquire();
    brick.configure(x, y, cfg);
    const mask = this.gameviewMask();
    if (mask) brick.applyContentMask(mask.mask);
    brick.enableTargetInput();
  }
}
